"use client";

import { useState, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Edit, Trash2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, X, User, Calendar, DollarSign } from "lucide-react";
// import moment from "moment";
import moment from "moment-timezone";
import { isDateTimeInRange } from "../utils/timezone";
import { EmployeeDetailsContent } from "../components/EmployeeDetailsContent";

export const TournamentHouseFeeTransactionTable = ({
  transactions,
  currentPage,
  setCurrentPage,
  entriesPerPage,
  setEntriesPerPage,
  onEdit,
  onDelete,
  dateRange,
  employeeApiService,
  employees
}) => {
  // State for sorting and modals
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [modalEmployee, setModalEmployee] = useState(null);
  const [modalRecipient, setModalRecipient] = useState(null);

  // State for employee details modal
  const [employeeDetailsError, setEmployeeDetailsError] = useState(null);
  const [showEmployeeDetailsModal, setShowEmployeeDetailsModal] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);

  // Filter transactions by date range
  const filteredTransactions = useMemo(() => {
    if (!dateRange || !dateRange.start || !dateRange.end) {
      return transactions;
    }

    return transactions.filter((transaction) =>
      isDateTimeInRange(transaction.date, dateRange.start, dateRange.end)
    );
  }, [transactions, dateRange]);

  // Sort transactions
  const sortedTransactions = useMemo(() => {
    const sortableTransactions = [...filteredTransactions];

    if (sortConfig.key) {
      sortableTransactions.sort((a, b) => {
        let aValue = a[sortConfig.key] || '';
        let bValue = b[sortConfig.key] || '';

        // Handle date sorting
        if (sortConfig.key === 'date') {
          aValue = moment(aValue, "MM/DD/YYYY hh:mm A").valueOf();
          bValue = moment(bValue, "MM/DD/YYYY hh:mm A").valueOf();
        }

        // Handle numeric sorting for amounts
        if (sortConfig.key === 'transaction_amount' || sortConfig.key === 'before_balance' || sortConfig.key === 'after_balance') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableTransactions;
  }, [filteredTransactions, sortConfig]);

  // Calculate pagination values
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = sortedTransactions.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(sortedTransactions.length / entriesPerPage);

  // Handle employee name click (Added By)
  const handleEmployeeNameClick = (employeeName, e) => {
    e.stopPropagation();
    setModalEmployee(employeeName);
    setShowEmployeeModal(true);
  };


  const closeEmployeeDetailsModal = () => {
    setShowEmployeeDetailsModal(false);
    setEmployeeDetails(null);
    setEmployeeDetailsError(null);
    setShowEmployeeModal(false)
  };

  const closeRecipientModal = () => {
    setModalRecipient(null);
    setShowEmployeeModal(false)
  };


  // Request sort
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Format number with commas
  const formatNumberWithCommas = (amount) => {
    const numericAmount = parseFloat(amount);
    return numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Format amount with sign and color
  const formatAmount = (amount, type) => {
    const sign = type === "deposit" ? "+" : "-";
    const color = type === "deposit" ? "text-green-600" : "text-red-600";
    return (
      <span className={`font-semibold ${color}`}>
        {sign}${formatNumberWithCommas(amount)}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    // Date is already in MM/DD/YYYY hh:mm A format, so display as is
    return dateString;
  };

  // Generate pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="icon"
          className="h-8 w-8"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>
      );
    }

    return buttons;
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortConfig.key === column) {
      return sortConfig.direction === "ascending" ?
        <ChevronDown className="inline-block ml-1 h-4 w-4 rotate-180" /> :
        <ChevronDown className="inline-block ml-1 h-4 w-4" />;
    }
    return <ChevronDown className="inline-block ml-1 h-4 w-4 opacity-30" />;
  };

  // Get employee statistics (Added By)
  const getEmployeeStats = (employeeName) => {
    const employeeTransactions = filteredTransactions.filter(t => t.added_by === employeeName);
    const totalTransactions = employeeTransactions.length;
    const totalAmount = employeeTransactions.reduce((sum, t) => sum + Math.abs(parseFloat(t.transaction_amount || 0)), 0);
    const deposits = employeeTransactions.filter(t => t.transaction_type === 'deposit').length;
    const withdrawals = employeeTransactions.filter(t => t.transaction_type === 'withdraw').length;
    const latestTransaction = employeeTransactions.length > 0 ?
      employeeTransactions.sort((a, b) => moment(b.date, "MM/DD/YYYY hh:mm A").valueOf() - moment(a.date, "MM/DD/YYYY hh:mm A").valueOf())[0] : null;

    return {
      totalTransactions,
      totalAmount,
      deposits,
      withdrawals,
      latestTransaction
    };
  };


  return (
    <>
      <div className="bg-white rounded-md shadow overflow-hidden">
        {/* Show date range info if applied */}
        {dateRange && dateRange.start && dateRange.end && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
            <p className="text-sm text-blue-800">
              <Calendar className="inline h-4 w-4 mr-1" />
              Showing transactions from {moment(dateRange.start).format("MM/DD/YYYY")} to {moment(dateRange.end).format("MM/DD/YYYY")}
              <span className="ml-2 text-blue-600">({filteredTransactions.length} total transactions)</span>
            </p>
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer" onClick={() => requestSort("date")}>
                Date
                {renderSortIcon("date")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("before_balance")}>
                Tournament House Fee Balance
                {renderSortIcon("before_balance")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("transaction_amount")}>
                Transaction
                {renderSortIcon("transaction_amount")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("notes")}>
                Transaction Details
                {renderSortIcon("notes")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("after_balance")}>
                After Balance
                {renderSortIcon("after_balance")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("added_by")}>
                Added By
                {renderSortIcon("added_by")}
              </TableHead>

              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                  {filteredTransactions.length === 0 ? "No transactions found for the selected date range" : "No transactions found"}
                </TableCell>
              </TableRow>
            ) : (
              currentEntries.map((transaction) => (
                <TableRow key={transaction.id} className="hover:bg-gray-50">
                  <TableCell>
                    {formatDate(transaction.date)}
                  </TableCell>
                  <TableCell>${formatNumberWithCommas(transaction.before_balance)}</TableCell>
                  <TableCell>
                    {formatAmount(transaction.transaction_amount, transaction.transaction_type)}
                  </TableCell>
                  <TableCell>{transaction.notes || '-'}</TableCell>

                  <TableCell>${formatNumberWithCommas(transaction.after_balance)}</TableCell>
                  <TableCell>
                    <button
                      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                      onClick={(e) => handleEmployeeNameClick(transaction.added_by, e)}
                    >
                      {transaction.added_by || '-'}
                    </button>
                  </TableCell>
      
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-blue-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(transaction);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(transaction);
                        }}
                        className={!transaction.isDeletable ? "opacity-50 cursor-not-allowed h-8 w-8 text-red-600" : "h-8 w-8 text-red-600"}
                        disabled={!transaction.isDeletable}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination Controls */}
        <div className="flex justify-between items-center mt-4 text-sm px-4 py-2 border-t">
          <div>
            Showing {sortedTransactions.length > 0 ? indexOfFirstEntry + 1 : 0} to{" "}
            {Math.min(indexOfLastEntry, sortedTransactions.length)} of {sortedTransactions.length} entries
          </div>

          <div className="flex items-center gap-2">
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <span>entries per page</span>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {renderPaginationButtons()}

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Employee Details Modal (Added By) */}
      {showEmployeeModal && modalEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{modalEmployee}</h2>
                  <p className="text-sm text-gray-500">
                    Employee Tournament House Fee Transaction Details
                    {dateRange && dateRange.start && dateRange.end && (
                      <span className="ml-2">
                        ({moment(dateRange.start).format("MM/DD/YYYY")} - {moment(dateRange.end).format("MM/DD/YYYY")})
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeRecipientModal}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Total Transactions</p>
                      <p className="text-xl font-bold text-blue-800">
                        {getEmployeeStats(modalEmployee).totalTransactions}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-green-600 font-medium">Total Amount</p>
                      <p className="text-xl font-bold text-green-800">
                        ${formatNumberWithCommas(getEmployeeStats(modalEmployee).totalAmount)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <ChevronDown className="h-4 w-4 text-emerald-600 rotate-180" />
                    </div>
                    <div>
                      <p className="text-sm text-emerald-600 font-medium">Deposits</p>
                      <p className="text-xl font-bold text-emerald-800">
                        {getEmployeeStats(modalEmployee).deposits}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <ChevronDown className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-red-600 font-medium">Withdrawals</p>
                      <p className="text-xl font-bold text-red-800">
                        {getEmployeeStats(modalEmployee).withdrawals}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Latest Transaction Card */}
              {getEmployeeStats(modalEmployee).latestTransaction && (
                <div className="bg-gray-50 p-4 rounded-lg border mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Latest Transaction</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Date</p>
                      <p className="font-medium">
                        {formatDate(getEmployeeStats(modalEmployee).latestTransaction.date)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Amount</p>
                      <p className="font-medium">
                        {formatAmount(
                          getEmployeeStats(modalEmployee).latestTransaction.transaction_amount,
                          getEmployeeStats(modalEmployee).latestTransaction.transaction_type
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Tournament House Fee Given To</p>
                      <p className="font-medium text-blue-600">
                        {getEmployeeStats(modalEmployee).latestTransaction.notes || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Transactions Table */}
              <div className="bg-white rounded-lg border">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-800">Recent Transactions</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction Details</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredTransactions
                        .filter(t => t.added_by === modalEmployee)
                        .sort((a, b) => moment(b.date, "MM/DD/YYYY hh:mm A").valueOf() - moment(a.date, "MM/DD/YYYY hh:mm A").valueOf())
                        .slice(0, 10)
                        .map((transaction, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {formatDate(transaction.date)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {transaction.notes || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {formatAmount(transaction.transaction_amount, transaction.transaction_type)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <Button onClick={closeRecipientModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Recipient Details Modal (Cash Given To) */}
      {showEmployeeDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {employeeDetails?.employee?.name || modalRecipient}
                    (
                    <span>{(employeeDetails?.employee?.role || '')}</span>)
                  </h2>
                  <p className="text-sm text-gray-500">
                    Employee Details
                    {dateRange && dateRange.start && dateRange.end && (
                      <span className="ml-2">
                        ({moment(dateRange.start).format("MM/DD/YYYY")} - {moment(dateRange.end).format("MM/DD/YYYY")})
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeEmployeeDetailsModal}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="p-6">
              {employeeDetailsError ? (
                <div className="text-center py-8 text-red-600">
                  {employeeDetailsError}
                </div>
              ) : employeeDetails ? (
                <>
                  
                                    <EmployeeDetailsContent employeeDetails={employeeDetails} />
                                
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <User className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Details Found</h3>
                  <p className="text-gray-500 text-center max-w-md">
                    No employee details are available for the selected period.
                    Please check if the employee has any recorded activity during this time.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <Button onClick={closeEmployeeDetailsModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};